// features/workstations/catalyst/track-record-section.js
// Track Record section — scored count vs excluded count, sustain
// rate vs base rate, calibration plot stub. Empty until Phase 9.

import { useEffect, useState } from 'react';

export default function TrackRecordSection({ userId }) {
  const [data, setData] = useState({ stats: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/catalyst/list-predictions${qs}&stats=1`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const payload = await res.json();
        if (cancelled) return;
        setData({ stats: payload.stats || null, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setData((s) => ({ ...s, loading: false, error: err.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (data.loading) {
    return <div className="cat-section"><div className="cat-loading">Loading track record…</div></div>;
  }
  if (data.error) {
    return (
      <div className="cat-section">
        <div className="cat-error">Could not load track record: {data.error}</div>
      </div>
    );
  }

  const stats = data.stats || { total: 0, scored: 0, excluded: 0, sustained: 0, faded: 0 };
  const sustainRate = stats.scored > 0 ? (stats.sustained / stats.scored) : null;

  return (
    <div className="cat-section">
      <div className="cat-block">
        <div className="cat-block-title">Track Record (catalyst-v2.0)</div>
        <div className="cat-block-subtitle">
          Per H-001v2 Bound 6, the <strong>excluded count</strong> is reported alongside the scored count —
          the bias from TV-only price sourcing is visible, not concealed. The sustain rate
          shown is computed on the <strong>scored subset only</strong>.
        </div>

        <div className="cat-stat-grid">
          <StatTile label="Predictions locked"      value={stats.total} />
          <StatTile label="Scored (D+30 reached)"   value={stats.scored} />
          <StatTile label="Excluded (TV gap)"       value={stats.excluded} sub="full-dissolution tail" />
          <StatTile label="Sustained (D+30)"        value={stats.sustained} />
          <StatTile label="Faded (D+30)"            value={stats.faded} />
          <StatTile
            label="Sustain rate"
            value={sustainRate === null ? '—' : `${(sustainRate * 100).toFixed(1)}%`}
            sub={sustainRate === null ? 'awaiting outcomes' : 'on scored subset'}
          />
        </div>
      </div>

      <div className="cat-block">
        <div className="cat-block-title">Calibration plot</div>
        <div className="cat-block-subtitle">
          Predicted sustain probability vs observed sustain frequency, in decile bins.
          Diagonal = perfect calibration. Auto-generated weekly from
          <code> catalyst_predictions × catalyst_outcomes</code>.
        </div>
        <div className="cat-empty">
          <div style={{ color: '#c8a96e', marginBottom: 8 }}>Calibration plot ships in Phase 9.</div>
          <div>
            The plot is auto-generated weekly once predictions start resolving (~T+12 weeks
            from H-001v2 lock 2026-05-07). Until then, this panel is empty.
          </div>
        </div>
      </div>

      <div className="cat-block">
        <div className="cat-block-title">Per-feature sustain rates</div>
        <div className="cat-block-subtitle">
          Hit rate stratified by each pre-event feature. Tells us which features
          actually carry signal. Per H-001v2 validation criteria, child hypotheses
          require ≥30 events scored before validation can be evaluated.
        </div>
        <div className="cat-empty">
          Awaiting first 30 scored predictions. Currently 0 of 30.
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="cat-stat-tile">
      <div className="cat-stat-label">{label}</div>
      <div className="cat-stat-value">{value}</div>
      {sub && <div className="cat-stat-sub">{sub}</div>}
    </div>
  );
}
